/**
 * Settings, in four groups by who a change affects. Your family (shared with
 * the household): the plan (trial, purchase), babies (with the birth and due
 * dates and the sex the growth curves need), bottle, weight, and meds presets,
 * what the family tracks, carers sharing the log. Your account: connecting an
 * AI assistant (a link to /assistant) and removing one this parent allowed,
 * sign out. This device: bottle alerts, night mode, usage analytics, the
 * running version with a reload. Then feedback to Babia's makers. Each group
 * has an id, so other pages link into it (`/settings#device`). Plain forms
 * that save on submit (switches save as they flip) — nothing here is a 3am
 * flow.
 */
import {
  ageText,
  BOTTLE_PRESETS,
  BOTTLE_UNIT_LABEL,
  BOTTLE_UNITS,
  type BottleUnit,
  DUE_AFTER_BIRTH_DAYS,
  DUE_BEFORE_BIRTH_DAYS,
  dueDateRange,
  earliestBirthDate,
  fromOz,
  isPreterm,
  localCalendar,
  MAX_AGE_YEARS,
  MAX_BABIES,
  presetProblem,
  SEX_LABEL,
  type Sex,
  SEXES,
  toOz,
  type TrackableType,
  WEIGHT_UNIT_LABEL,
  WEIGHT_UNITS,
  type WeightUnit,
} from "@babia/checks";
import { Link } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Check, Copy, Sparkles, X } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { BuyButton } from "../components/buy-button.tsx";
import { FeedbackButton } from "../components/feedback.tsx";
import { Button } from "../components/ui/button.tsx";
import { LegalLinks } from "../components/legal-links.tsx";
import { Section as Group } from "../components/overview/section.tsx";
import { Input, Label } from "../components/ui/input.tsx";
import { useToast } from "../components/ui/toast.tsx";
import {
  analyticsAllowed,
  analyticsEnabled,
  resetAnalytics,
  setAnalyticsAllowed,
  track,
} from "../lib/analytics.ts";
import { useAppUpdate } from "../lib/app-update.tsx";
import { ABILITIES, assistantName } from "../lib/assistants.ts";
import { PRETERM_HINT } from "../lib/growth.ts";
import { signOut, useSession } from "../lib/auth-client.ts";
import {
  DOSE_UNIT_LABEL,
  DOSE_UNITS,
  type DoseUnit,
  MAX_DOSE,
  parseDose,
} from "../lib/entry-text.ts";
import { useNight } from "../lib/night.tsx";
import {
  canLog,
  type PlanLike,
  priceLabel,
  REGULAR_PRICE_CENTS,
  trialDaysLeft,
} from "../lib/plan.ts";
import { devicePushSupport } from "../lib/push.ts";
import { alertsView } from "../lib/push-state.ts";
import { CLIENT_VERSION, shortVersion } from "../lib/update.ts";
import {
  type Baby,
  type ConnectedAssistant,
  forgetPushDevice,
  type Household,
  phoneTimeZone,
  useAssistants,
  useBabies,
  useCreateBabies,
  useCreateInvite,
  useDeleteBaby,
  useHousehold,
  useJoinHousehold,
  usePushConfig,
  usePushDevice,
  useRecheckPushDevice,
  useRemoveAssistant,
  useReorderBabies,
  useTurnOffPush,
  useTurnOnPush,
  useUpdateBaby,
  useUpdateHousehold,
} from "../lib/queries.ts";
import { clock, dateline, pluralize, toDate } from "../lib/time.ts";
import { browserZones, currentZoneName, zoneGroups, zoneLabel } from "../lib/time-zones.ts";
import { useNow } from "../lib/use-now.ts";
import { cn } from "../lib/utils.ts";

/**
 * A kicker section inside a group. The first sits close under the group's
 * rule; the next ones keep the wider gap (a hidden section renders nothing,
 * so the gap counts from the one before it).
 */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6 [&+&]:mt-9">
      <h3 className="kicker">{title}</h3>
      <div className="mt-2.5">{children}</div>
    </section>
  );
}

// --- Babies ----------------------------------------------------------------

/** Beside the due date: the corrected age, or what the date is for. */
function dueText(baby: Baby, today: string): string {
  if (!baby.dueDate) return PRETERM_HINT;
  if (!baby.birthDate || !isPreterm(baby.birthDate, baby.dueDate))
    return "Within 3 weeks of the birthday, so ages count from birth";
  return today < baby.dueDate
    ? "Corrected age starts on the due date"
    : `${ageText(baby.dueDate, today)}, corrected`;
}

function BabyRow({
  baby,
  siblings,
  index,
  count,
  onMove,
}: {
  baby: Baby;
  /** The other babies: twins share a due date. */
  siblings: Baby[];
  index: number;
  count: number;
  onMove: (from: number, to: number) => void;
}) {
  const toast = useToast();
  const update = useUpdateBaby();
  const remove = useDeleteBaby();
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const name = nameDraft ?? baby.name;
  const [aliasDraft, setAliasDraft] = useState<string | null>(null);
  const aliasText = aliasDraft ?? baby.aliases.join(", ");
  const [birthDraft, setBirthDraft] = useState<string | null>(null);
  const birthDateId = useId();
  const [dueDraft, setDueDraft] = useState<string | null>(null);
  const dueDateId = useId();
  // The chip shows a tap at once; the saved sex replaces it when the server answers.
  const [sexDraft, setSexDraft] = useState<Sex | null | undefined>(undefined);
  const sex = sexDraft === undefined ? baby.sex : sexDraft;
  const sexId = useId();
  const today = localCalendar.isoDate(new Date());
  const dueRange = dueDateRange(baby.birthDate, today);
  const [confirming, setConfirming] = useState(false);

  function save() {
    const trimmed = name.trim();
    if (!trimmed || trimmed === baby.name) {
      setNameDraft(null);
      return;
    }
    update.mutate(
      { id: baby.id, name: trimmed },
      {
        onSuccess: () => setNameDraft((current) => (current === nameDraft ? null : current)),
        onError: (err) => toast.show(err.message),
      },
    );
  }

  function saveAliases() {
    if (aliasDraft === null) return;
    const aliases = aliasDraft
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean)
      .map((a) => a.slice(0, 40));
    if (aliases.join(",") === baby.aliases.join(",")) {
      setAliasDraft(null);
      return;
    }
    update.mutate(
      { id: baby.id, aliases },
      {
        onSuccess: () => setAliasDraft((current) => (current === aliasDraft ? null : current)),
        onError: (err) => toast.show(err.message),
      },
    );
  }

  /** Saves on blur, like the names: a desktop date field passes through odd years while typed. */
  function saveBirthDate(input: HTMLInputElement) {
    // A half-typed date reads as empty, and a typed one can pass the picker's
    // range: keep the saved one rather than clearing or replacing it.
    const { badInput, rangeOverflow, rangeUnderflow } = input.validity;
    if (rangeOverflow) toast.show("A birthday can't be in the future");
    if (rangeUnderflow) toast.show(`A birthday can't be more than ${MAX_AGE_YEARS} years ago`);
    if (birthDraft === null || badInput || rangeOverflow || rangeUnderflow) {
      setBirthDraft(null);
      return;
    }
    const birthDate = birthDraft || null;
    if (birthDate === baby.birthDate) {
      setBirthDraft(null);
      return;
    }
    update.mutate(
      { id: baby.id, birthDate },
      {
        onSuccess: () => setBirthDraft((current) => (current === birthDraft ? null : current)),
        onError: (err) => toast.show(err.message),
      },
    );
  }

  /** Saves on blur, as the birthday does; the first due date fills in a twin's. */
  function saveDueDate(input: HTMLInputElement) {
    const { badInput, rangeOverflow, rangeUnderflow } = input.validity;
    if (rangeOverflow)
      toast.show(
        baby.birthDate
          ? `A due date can't be more than ${DUE_AFTER_BIRTH_DAYS / 7} weeks after the birthday`
          : `A due date can't be more than ${DUE_AFTER_BIRTH_DAYS / 7} weeks ahead`,
      );
    if (rangeUnderflow)
      toast.show(
        baby.birthDate
          ? `A due date can't be more than ${DUE_BEFORE_BIRTH_DAYS / 7} weeks before the birthday`
          : `A due date can't be more than ${MAX_AGE_YEARS} years ago`,
      );
    if (dueDraft === null || badInput || rangeOverflow || rangeUnderflow) {
      setDueDraft(null);
      return;
    }
    const dueDate = dueDraft || null;
    if (dueDate === baby.dueDate) {
      setDueDraft(null);
      return;
    }
    // Twins share a due date: fill it in for each sibling born the same day without one.
    const twins =
      dueDate && baby.birthDate
        ? siblings.filter((b) => b.birthDate === baby.birthDate && !b.dueDate)
        : [];
    update.mutate(
      { id: baby.id, dueDate },
      {
        onSuccess: () => {
          setDueDraft((current) => (current === dueDraft ? null : current));
          if (!twins.length) return;
          // Say what happened only once every twin's write has answered.
          void Promise.allSettled(
            twins.map((twin) => update.mutateAsync({ id: twin.id, dueDate })),
          ).then((results) => {
            const names = (list: Baby[]) => list.map((b) => b.name).join(" and ");
            const failed = twins.filter((_, i) => results[i]!.status === "rejected");
            const set = twins.filter((_, i) => results[i]!.status === "fulfilled");
            toast.show(
              failed.length
                ? `Couldn't set the same due date for ${names(failed)}. Try again there.`
                : `Same due date set for ${names(set)}`,
            );
          });
        },
        onError: (err) => toast.show(err.message),
      },
    );
  }

  function pickSex(next: Sex) {
    const value = sex === next ? null : next;
    setSexDraft(value);
    update.mutate(
      { id: baby.id, sex: value },
      {
        onSettled: () => setSexDraft((current) => (current === value ? undefined : current)),
        onError: (err) => toast.show(err.message),
      },
    );
  }

  return (
    <li className="flex flex-col gap-2 border-t border-rule py-3">
      <div className="flex items-center gap-2">
        <Input
          value={name}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          maxLength={40}
          aria-label={`Name of baby ${index + 1}`}
        />
        <Button
          variant="icon"
          disabled={index === 0}
          onClick={() => onMove(index, index - 1)}
          aria-label="Move up"
        >
          <ArrowUp className="size-4" />
        </Button>
        <Button
          variant="icon"
          disabled={index === count - 1}
          onClick={() => onMove(index, index + 1)}
          aria-label="Move down"
        >
          <ArrowDown className="size-4" />
        </Button>
      </div>
      <Input
        value={aliasText}
        onChange={(e) => setAliasDraft(e.target.value)}
        onBlur={saveAliases}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
        placeholder="Nicknames for the AI assistant, e.g. August, Gus"
        aria-label={`Nicknames for ${baby.name}`}
        className="text-[14px]"
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Label htmlFor={birthDateId} className="min-w-10">
          Born
        </Label>
        <Input
          id={birthDateId}
          type="date"
          value={birthDraft ?? baby.birthDate ?? ""}
          min={earliestBirthDate(today)}
          max={today}
          onChange={(e) => setBirthDraft(e.target.value)}
          onBlur={(e) => saveBirthDate(e.currentTarget)}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          aria-describedby={`${birthDateId}-age`}
          className="w-[10.5rem]"
        />
        <span id={`${birthDateId}-age`} className="text-[13px] opacity-65">
          {baby.birthDate ? ageText(baby.birthDate, today) : "Add a birthday to see the age"}
        </span>
      </div>
      {baby.birthDate || baby.dueDate ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Label htmlFor={dueDateId} className="min-w-10">
            Due
          </Label>
          <Input
            id={dueDateId}
            type="date"
            value={dueDraft ?? baby.dueDate ?? ""}
            min={dueRange.min}
            max={dueRange.max}
            onChange={(e) => setDueDraft(e.target.value)}
            onBlur={(e) => saveDueDate(e.currentTarget)}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            aria-describedby={`${dueDateId}-corrected`}
            className="w-[10.5rem]"
          />
          <span id={`${dueDateId}-corrected`} className="text-[13px] opacity-65">
            {dueText(baby, today)}
          </span>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span id={sexId} className="kicker min-w-10">
          Sex
        </span>
        <div role="group" aria-labelledby={sexId} className="flex gap-1.5">
          {SEXES.map((option) => (
            <Button
              key={option}
              variant="chip"
              className={cn("w-[72px] flex-none text-[14px]", sex === option && "is-selected")}
              aria-pressed={sex === option}
              onClick={() => pickSex(option)}
            >
              {SEX_LABEL[option]}
            </Button>
          ))}
        </div>
        <span className="text-[13px] opacity-65">
          Only for the WHO growth curves, which differ for girls and boys
        </span>
      </div>
      {confirming ? (
        <div className="flex items-center gap-2 text-[13px]">
          <span className="opacity-70">Delete {baby.name} and every entry?</span>
          <Button
            variant="ghost"
            className="text-accent-2"
            onClick={() =>
              remove.mutate(baby.id, {
                onError: (err) => toast.show(err.message),
              })
            }
          >
            Delete
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Keep
          </Button>
        </div>
      ) : count > 1 ? (
        <Button
          variant="ghost"
          className="-ml-2 self-start text-[13px] opacity-60"
          onClick={() => setConfirming(true)}
        >
          Remove
        </Button>
      ) : null}
    </li>
  );
}

function Babies() {
  const babies = useBabies();
  const reorder = useReorderBabies();
  const create = useCreateBabies();
  const toast = useToast();
  const [newName, setNewName] = useState("");
  const list = babies.data ?? [];

  function move(from: number, to: number) {
    const ids = list.map((b) => b.id);
    const [id] = ids.splice(from, 1);
    ids.splice(to, 0, id!);
    reorder.mutate(ids, { onError: (err) => toast.show(err.message) });
  }

  function add(e: React.FormEvent) {
    e.preventDefault();
    const n = newName.trim();
    if (!n) return;
    create.mutate([{ name: n }], {
      onSuccess: () => setNewName(""),
      onError: (err) => toast.show(err.message),
    });
  }

  return (
    <Section title="Babies">
      <ul>
        {list.map((b, i) => (
          <BabyRow
            key={b.id}
            baby={b}
            siblings={list.filter((other) => other.id !== b.id)}
            index={i}
            count={list.length}
            onMove={move}
          />
        ))}
      </ul>
      {list.length < MAX_BABIES ? (
        <form onSubmit={add} className="mt-2 flex gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Add a baby"
            maxLength={40}
            aria-label="New baby name"
          />
          <Button
            type="submit"
            variant="secondary"
            className="shrink-0 px-5"
            disabled={!newName.trim()}
          >
            Add
          </Button>
        </form>
      ) : null}
    </Section>
  );
}

// --- Presets ---------------------------------------------------------------

const MAX_MEDS = 8;

/** A medicine row as typed: the dose stays text until Save. */
interface MedDraft {
  name: string;
  amount: string;
  unit: DoseUnit;
}

const toMedDraft = ({ name, dose }: Household["medPresets"][number]): MedDraft => ({
  name,
  amount: dose ? String(dose.amount) : "",
  unit: dose?.unit ?? "ml",
});

/** Each medicine with its usual dose, the one the meds sheet logs in one tap. */
function MedsEditor({
  meds,
  onChange,
}: {
  meds: MedDraft[];
  onChange: (meds: MedDraft[]) => void;
}) {
  const set = (i: number, patch: Partial<MedDraft>) =>
    onChange(meds.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  return (
    <fieldset>
      <legend className="kicker">Meds and usual doses</legend>
      <p className="mt-1 text-[13px] opacity-65">
        Tapping a medicine on the log saves its usual dose. Leave the dose blank to log the name
        alone.
      </p>
      <ul className="mt-1">
        {meds.map((m, i) => (
          <li key={i} className="mt-2 flex items-center gap-2">
            <Input
              value={m.name}
              onChange={(e) => set(i, { name: e.target.value })}
              placeholder="Medicine"
              maxLength={40}
              aria-label={`Medicine ${i + 1}`}
              className="min-w-0 flex-1"
            />
            <Input
              value={m.amount}
              onChange={(e) => set(i, { amount: e.target.value })}
              placeholder="Dose"
              inputMode="decimal"
              autoComplete="off"
              aria-label={`Usual dose of ${m.name.trim() || `medicine ${i + 1}`}`}
              aria-invalid={m.amount.trim() !== "" && parseDose(m.amount) === null}
              className="w-[68px] shrink-0 text-right tabular-nums"
            />
            <select
              value={m.unit}
              onChange={(e) => set(i, { unit: e.target.value as DoseUnit })}
              aria-label={`Unit for ${m.name.trim() || `medicine ${i + 1}`}`}
              className={cn("input w-[84px] shrink-0 px-2", !m.amount.trim() && "opacity-55")}
            >
              {DOSE_UNITS.map((u) => (
                <option key={u} value={u}>
                  {DOSE_UNIT_LABEL[u]}
                </option>
              ))}
            </select>
            <Button
              variant="icon"
              className="shrink-0"
              disabled={meds.length === 1}
              onClick={() => onChange(meds.filter((_, j) => j !== i))}
              aria-label={`Remove ${m.name.trim() || `medicine ${i + 1}`}`}
            >
              <X className="size-4" />
            </Button>
          </li>
        ))}
      </ul>
      {meds.length < MAX_MEDS ? (
        <Button
          variant="ghost"
          className="-ml-2 mt-1 text-[14px]"
          onClick={() => onChange([...meds, { name: "", amount: "", unit: "ml" }])}
        >
          + Add a medicine
        </Button>
      ) : null}
    </fieldset>
  );
}

/**
 * The household's zone, picked from the browser's list by place and name, or
 * typed as an IANA name where the browser can't list its zones.
 */
function TimeZoneField({ zone, onChange }: { zone: string; onChange: (zone: string) => void }) {
  const phoneZone = phoneTimeZone();
  const shown = currentZoneName(zone.trim());
  const groups = useMemo(() => {
    const zones = browserZones();
    return zones && zoneGroups(zones, [shown]);
  }, [shown]);
  return (
    <div>
      <Label htmlFor="time-zone">Time zone (for AI assistant replies)</Label>
      {groups ? (
        <select
          id="time-zone"
          value={shown}
          onChange={(e) => onChange(e.target.value)}
          className="input mt-1"
        >
          {shown ? null : (
            <option value="" disabled>
              Choose a time zone
            </option>
          )}
          {groups.map((group) => (
            <optgroup key={group.region} label={group.region}>
              {group.zones.map((z) => (
                <option key={z.value} value={z.value}>
                  {z.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      ) : (
        <Input
          id="time-zone"
          className="mt-1"
          value={zone}
          onChange={(e) => onChange(e.target.value)}
          placeholder={phoneZone ?? "America/Chicago"}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
        />
      )}
      {phoneZone && currentZoneName(phoneZone) !== shown ? (
        <Button
          type="button"
          variant="ghost"
          className="-ml-2 mt-1 text-[13px] opacity-70"
          onClick={() => onChange(currentZoneName(phoneZone))}
        >
          Use this phone's: {zoneLabel(currentZoneName(phoneZone))}
        </Button>
      ) : null}
    </div>
  );
}

function Presets() {
  const household = useHousehold();
  const update = useUpdateHousehold();
  const toast = useToast();
  const saved = household.data;
  const savedUnit = saved?.bottleUnit ?? "oz";
  const savedWeightUnit = saved?.weightUnit ?? "lb";
  /** The saved sizes as text in `unit`, or the unit's usual sizes when it isn't the saved one. */
  const sizesIn = (unit: BottleUnit) =>
    saved && unit === saved.bottleUnit
      ? saved.ozPresets.map((oz) => fromOz(oz, unit)).join(", ")
      : BOTTLE_PRESETS[unit].join(", ");
  // Untouched forms follow the server; a parent's draft survives partner updates.
  const [draft, setDraft] = useState<{
    feedLabel: string;
    unit: BottleUnit;
    sizes: string;
    weightUnit: WeightUnit;
    meds: MedDraft[];
    zone: string;
  } | null>(null);
  const values = draft ?? {
    feedLabel: saved?.feedLabel ?? "",
    unit: savedUnit,
    sizes: saved ? sizesIn(savedUnit) : "",
    weightUnit: savedWeightUnit,
    meds: saved?.medPresets.map(toMedDraft) ?? [],
    zone: saved?.timeZone ?? "",
  };
  const { feedLabel, unit, sizes, weightUnit, meds, zone } = values;
  const example = BOTTLE_PRESETS[unit].join(", ");

  /** A switch starts the sizes over in the new unit (or back to the saved ones). */
  function pickUnit(next: BottleUnit) {
    setDraft({ ...values, unit: next, sizes: sizesIn(next) });
  }

  function save(e: React.FormEvent) {
    e.preventDefault();
    const typed = sizes.split(/[,\s]+/).filter(Boolean);
    const amounts = typed.map(Number);
    const named = meds.filter((m) => m.name.trim());
    const badDose = named.find((m) => m.amount.trim() && parseDose(m.amount) === null);
    const medPresets = named.map((m) => {
      const amount = parseDose(m.amount);
      return { name: m.name.trim(), dose: amount === null ? null : { amount, unit: m.unit } };
    });
    if (amounts.length === 0) return toast.show(`List at least one bottle size, e.g. ${example}`);
    if (amounts.some((n) => !Number.isFinite(n)))
      return toast.show(`Bottle sizes are numbers, e.g. ${example}`);
    const bad = amounts.find((n) => presetProblem(n, unit) !== null);
    if (bad !== undefined) return toast.show(`Bottle size ${bad}: ${presetProblem(bad, unit)}`);
    if (medPresets.length === 0) return toast.show("List at least one med");
    if (badDose)
      return toast.show(
        `Check the dose for ${badDose.name.trim()}: a number up to ${MAX_DOSE}, like 2.5`,
      );
    if (!zone.trim() && household.data?.timeZone)
      return toast.show("Enter a time zone, e.g. America/Chicago");
    update.mutate(
      {
        feedLabel: feedLabel.trim() || "Similac",
        bottleUnit: unit,
        ozPresets: amounts.map((amount) => toOz(amount, unit)),
        weightUnit,
        medPresets,
        ...(zone.trim() ? { timeZone: zone.trim() } : {}),
      },
      {
        onSuccess: () => {
          setDraft((current) => (current === draft ? null : current));
          toast.show("Saved");
        },
        onError: (err) => toast.show(err.message),
      },
    );
  }

  return (
    <Section title="Bottles, weights & meds">
      <form onSubmit={save} className="flex flex-col gap-4">
        <div>
          <Label htmlFor="feed-label">Formula / milk label</Label>
          <Input
            id="feed-label"
            className="mt-1"
            value={feedLabel}
            onChange={(e) => setDraft({ ...values, feedLabel: e.target.value })}
            maxLength={40}
          />
        </div>
        <fieldset>
          <legend className="kicker">Bottle amounts</legend>
          <div className="mt-1 flex flex-wrap gap-x-6">
            {BOTTLE_UNITS.map((u) => (
              <label key={u} className="flex min-h-[44px] items-center gap-2 text-[15px]">
                <input
                  type="radio"
                  name="bottle-unit"
                  value={u}
                  checked={unit === u}
                  onChange={() => pickUnit(u)}
                  className="size-5 accent-[var(--accent)]"
                />
                {BOTTLE_UNIT_LABEL[u]}
              </label>
            ))}
          </div>
          <p className="text-[13px] opacity-65">
            For everyone in the family and your assistant. Switching starts the sizes below over in
            the new unit; bottles already logged are converted.
          </p>
        </fieldset>
        <div>
          <Label htmlFor="bottle-presets">
            Bottle sizes ({unit === "oz" ? "oz, half-ounce steps" : "ml"})
          </Label>
          <Input
            id="bottle-presets"
            className="mt-1"
            value={sizes}
            onChange={(e) => setDraft({ ...values, sizes: e.target.value })}
            placeholder={example}
            autoComplete="off"
            inputMode="decimal"
          />
        </div>
        <fieldset>
          <legend className="kicker">Weights</legend>
          <div className="mt-1 flex flex-wrap gap-x-6">
            {WEIGHT_UNITS.map((u) => (
              <label key={u} className="flex min-h-[44px] items-center gap-2 text-[15px]">
                <input
                  type="radio"
                  name="weight-unit"
                  value={u}
                  checked={weightUnit === u}
                  onChange={() => setDraft({ ...values, weightUnit: u })}
                  className="size-5 accent-[var(--accent)]"
                />
                {WEIGHT_UNIT_LABEL[u]}
              </label>
            ))}
          </div>
          <p className="text-[13px] opacity-65">
            The unit your clinic says weights in, for everyone in the family and your assistant.
            Weights already logged are converted.
          </p>
        </fieldset>
        <MedsEditor meds={meds} onChange={(next) => setDraft({ ...values, meds: next })} />
        <TimeZoneField zone={zone} onChange={(next) => setDraft({ ...values, zone: next })} />
        <Button
          type="submit"
          variant="secondary"
          className="self-start px-6"
          disabled={update.isPending}
        >
          {update.isPending ? "Saving…" : "Save"}
        </Button>
      </form>
    </Section>
  );
}

// --- What you track ---------------------------------------------------------

const TRACKING_CHOICES: { type: TrackableType; label: string; detail: string }[] = [
  {
    type: "sleep",
    label: "Sleep",
    detail: "The Fell asleep button, the sleep charts and milestones, and nap checks.",
  },
  {
    type: "diaper",
    label: "Diaper changes",
    detail: "The Change button, change counts, and overdue-change checks.",
  },
];

function Tracking() {
  const household = useHousehold();
  const update = useUpdateHousehold();
  const toast = useToast();
  // The household read pauses while a save is in flight, so show the choice being saved.
  const untracked =
    (update.isPending ? update.variables?.untrackedTypes : undefined) ??
    household.data?.untrackedTypes ??
    [];

  function toggle(type: TrackableType, on: boolean) {
    update.mutate(
      { untrackedTypes: on ? untracked.filter((t) => t !== type) : [...untracked, type] },
      { onSuccess: () => toast.show("Saved"), onError: (err) => toast.show(err.message) },
    );
  }

  return (
    <Section title="What you track">
      {TRACKING_CHOICES.map(({ type, label, detail }) => (
        <label key={type} className="flex min-h-[44px] items-start gap-3 py-1.5 text-[15px]">
          <input
            type="checkbox"
            checked={!untracked.includes(type)}
            disabled={!household.data || update.isPending}
            onChange={(e) => toggle(type, e.target.checked)}
            className="mt-0.5 size-5 shrink-0 accent-[var(--accent)]"
          />
          <span>
            {label}
            <span className="block text-[13px] opacity-65">{detail}</span>
          </span>
        </label>
      ))}
      <p className="mt-1 text-[13px] opacity-65">
        Bottles, medicine, and notes are always on. This applies to everyone in the family and to
        your AI assistant. Turning one off deletes nothing, and you can turn it back on anytime.
      </p>
    </Section>
  );
}

// --- Bottle alerts -------------------------------------------------------------

const ALERTS_BLOCKED =
  "Notifications are blocked for Babia on this device. Allow them in this browser's settings, then come back.";

const ALERTS_PAUSED =
  "Paused: the family's free trial ended, so Babia sends no alerts. They start again when anyone in the family buys Babia.";

/** Why the switch can't be used (or only turned off) on this device, for the views that have one. */
const ALERTS_REASON = {
  "home-screen":
    "On iPhone and iPad, alerts work once Babia is on your Home Screen (iOS 16.4 or later). In Safari, tap Share, then Add to Home Screen, then open Babia from there and turn alerts on.",
  unsupported: "This browser can't show notifications. Chrome, Edge, Firefox, and Safari can.",
  paused: ALERTS_PAUSED,
  "paused-on": ALERTS_PAUSED,
  blocked: ALERTS_BLOCKED,
  "blocked-on": ALERTS_BLOCKED,
} as const;

/**
 * Web Push to this device when a bottle looks missed (the overview's bottle
 * checks). Per device and per parent, like night mode, but the server keeps
 * the subscription so it can send.
 */
function BottleAlerts() {
  const config = usePushConfig();
  const household = useHousehold();
  const support = devicePushSupport();
  const configured = config.data?.enabled === true;
  const device = usePushDevice(
    configured && support === "ok" ? (config.data?.publicKey ?? null) : null,
  );
  const turnOn = useTurnOnPush();
  const turnOff = useTurnOffPush();
  const toast = useToast();
  const busy = turnOn.isPending || turnOff.isPending;

  const view = alertsView({
    configured,
    support,
    permission: device.data?.permission ?? null,
    subscribed: device.data?.subscribed ?? false,
    canLog: canLog(household.data?.plan),
  });
  if (view === "hidden") return null;
  const reason = view === "on" || view === "off" ? null : ALERTS_REASON[view];
  // Paused or blocked, a device the server still has can be turned off, but none turned on.
  const removable = view === "paused-on" || view === "blocked-on";
  const checked = view === "on" || removable;
  const locked = reason !== null && !removable;

  function toggle(on: boolean) {
    if (on) {
      // Asked inside the tap, before any await: Safari only asks then.
      const permission = Notification.requestPermission();
      turnOn.mutate(
        { permission },
        {
          onSuccess: () => toast.show("Bottle alerts are on for this device"),
          onError: (err) => toast.show(err.message),
        },
      );
    } else {
      turnOff.mutate(undefined, {
        onSuccess: () => toast.show("Bottle alerts are off for this device"),
        onError: (err) => toast.show(err.message),
      });
    }
  }

  return (
    <Section title="Bottle alerts">
      <label className="flex min-h-[44px] items-start gap-3 py-1.5 text-[15px]">
        <input
          type="checkbox"
          checked={checked}
          disabled={locked || device.isPending || busy}
          onChange={(e) => toggle(e.target.checked)}
          className="mt-0.5 size-5 shrink-0 accent-[var(--accent)]"
        />
        <span>
          Alert this device when a bottle looks missed
          <span className="block text-[13px] opacity-65">
            When no bottle is logged well past a baby's usual gap, or one twin had a bottle and the
            other has nothing logged. One alert per missed bottle.
          </span>
        </span>
      </label>
      {reason ? <p className="mt-1 text-[13px]">{reason}</p> : null}
      <p className="mt-1 text-[13px] opacity-65">
        Each person turns alerts on for their own phone. They can come at night: your phone's Focus
        or Do Not Disturb decides when they make a sound. Alerts show the baby's name on the lock
        screen.
      </p>
    </Section>
  );
}

// --- Plan ------------------------------------------------------------------

/** The prices on the landing page; the server's offer decides which one applies. */
const FOUNDING_NOTE = `That is the founding price for the first 100 families. After them, Babia costs ${priceLabel(REGULAR_PRICE_CENTS)}.`;

/** Back from Polar's checkout (`?purchase=done`), before its webhook lands. */
function returnedFromCheckout(): boolean {
  try {
    return new URLSearchParams(window.location.search).get("purchase") === "done";
  } catch {
    return false;
  }
}

function PaidLine({ plan }: { plan: PlanLike & { paidAt: Date | string | null } }) {
  if (plan.tier === "grandfathered")
    return (
      <p className="text-[15px]">
        Free for good. Your family joined while Babia was free for founding families.
      </p>
    );
  const paidOn = plan.paidAt ? ` on ${dateline(toDate(plan.paidAt))}` : "";
  return (
    <p className="text-[15px]">
      {plan.tier === "founding"
        ? `Paid once${paidOn}, at the founding price. Thank you for being one of the first families.`
        : `Paid once${paidOn}. Thank you for supporting Babia.`}{" "}
      Everyone in the family can use every feature, for good.
    </p>
  );
}

/** The trial, the one-time purchase, or the thank-you once it is paid. */
function FamilyPlan() {
  const household = useHousehold();
  const now = useNow(60_000);
  const [returned] = useState(returnedFromCheckout);
  const plan = household.data?.plan;
  if (!plan || plan.status === "free") return null;

  if (plan.status === "paid")
    return (
      <Section title="Babia for your family">
        {returned ? <p className="heading mb-1 text-[18px]">Thank you!</p> : null}
        <PaidLine plan={plan} />
      </Section>
    );

  if (returned)
    return (
      <Section title="Babia for your family">
        <p className="heading text-[18px]">Thank you!</p>
        <p className="mt-1 text-[15px]">
          Polar is confirming your payment. This page unlocks Babia within a minute. If it still
          says this after ten minutes, email support@babia.app.
        </p>
      </Section>
    );

  const left = trialDaysLeft(plan, now);
  const ends = plan.trialEndsAt ? dateline(toDate(plan.trialEndsAt)) : null;
  return (
    <Section title="Babia for your family">
      <p className="text-[15px]">
        {plan.status === "expired"
          ? `Your free trial ended${ends ? ` on ${ends}` : ""}. The log is read-only until someone in the family buys Babia. Every entry is kept.`
          : left === null || !ends
            ? "Free trial: 14 days from setup."
            : `Free trial: ${left === 0 ? "ends today" : `${pluralize(left, "day", "days")} left`}, until ${ends}.`}
      </p>
      {plan.offer ? (
        <>
          <p className="mt-1.5 text-[13px] opacity-65">
            Buy once for {priceLabel(plan.offer.priceCents)}, for everyone in the family: up to{" "}
            {plan.maxCarers} carers. No subscription.{" "}
            {plan.offer.tier === "founding" ? FOUNDING_NOTE : null}
          </p>
          <BuyButton offer={plan.offer} placement="settings" className="mt-3" />
        </>
      ) : null}
    </Section>
  );
}

// --- Carers ----------------------------------------------------------------

function Partner() {
  const household = useHousehold();
  const invite = useCreateInvite();
  const join = useJoinHousehold();
  const toast = useToast();
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      track("invite code copied");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.show("Couldn't copy — long-press the code instead");
    }
  }

  const members = household.data?.members ?? [];
  const maxCarers = household.data?.plan.maxCarers ?? 10;
  const full = members.length >= maxCarers;

  return (
    <Section title="Carers">
      <p className="text-[13px] opacity-65">
        {members.length} of {maxCarers}. Everyone here shares this log.
      </p>
      <ul className="mt-2 text-[15px]">
        {members.map((m) => (
          <li key={m.userId} className="flex items-baseline gap-2 border-t border-rule py-2">
            <span>{m.name}</span>
            <span className="truncate text-[13px] opacity-55">{m.email}</span>
          </li>
        ))}
      </ul>

      {/* A full household shows no code: a code made earlier can't be used now. */}
      {full ? (
        <p className="mt-3 text-[14px]">
          Your family has {maxCarers} carers, the most one household can have.
        </p>
      ) : invite.data ? (
        <div className="mt-4 border border-divider p-4">
          <div className="kicker">Share this code</div>
          <div className="mt-1 flex items-center gap-3">
            <span className="heading select-all text-[36px] tracking-[0.18em]">
              {invite.data.code}
            </span>
            <Button variant="icon" onClick={() => copy(invite.data.code)} aria-label="Copy code">
              {copied ? <Check className="size-5" /> : <Copy className="size-5" />}
            </Button>
          </div>
          <p className="mt-1 text-[13px] opacity-65">
            Works once, until {dateline(new Date(invite.data.expiresAt))} at{" "}
            {clock(new Date(invite.data.expiresAt))}. They sign in with Google or Facebook and enter
            it in Settings or on the first screen.
          </p>
        </div>
      ) : (
        <Button
          variant="secondary"
          className="mt-3 w-full"
          onClick={() => invite.mutate(undefined, { onError: (err) => toast.show(err.message) })}
          disabled={invite.isPending}
        >
          {invite.isPending ? "Making a code…" : "Invite a carer"}
        </Button>
      )}

      <form
        className="mt-6"
        onSubmit={(e) => {
          e.preventDefault();
          join.mutate(code, {
            onSuccess: () => window.location.assign("/"),
            onError: (err) => toast.show(err.message),
          });
        }}
      >
        <Label htmlFor="join-code">Have a code? Join a family's log</Label>
        <p className="mt-1 text-[13px] opacity-65">
          Only works from an account with no babies logged yet.
        </p>
        <div className="mt-2 flex gap-2">
          <Input
            id="join-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="ABC234"
            autoComplete="one-time-code"
            autoCapitalize="characters"
            spellCheck={false}
            className="uppercase tracking-[0.2em]"
          />
          <Button
            type="submit"
            variant="secondary"
            className="shrink-0 px-5"
            disabled={code.trim().length < 6 || join.isPending}
          >
            {join.isPending ? "Joining…" : "Join"}
          </Button>
        </div>
      </form>
    </Section>
  );
}

// --- AI assistants ---------------------------------------------------------

function AssistantRow({ assistant }: { assistant: ConnectedAssistant }) {
  const toast = useToast();
  const remove = useRemoveAssistant();
  const [confirming, setConfirming] = useState(false);
  const name = assistantName(assistant);

  return (
    <li className="border-t border-rule py-2">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="heading truncate text-[17px]">{name}</div>
          {assistant.connectedAt ? (
            <div className="text-[13px] opacity-55">
              Connected{" "}
              {assistant.connectedAt.toLocaleDateString(undefined, {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </div>
          ) : null}
        </div>
        {confirming ? null : (
          <Button
            variant="ghost"
            className="shrink-0 text-[13px] opacity-60"
            onClick={() => setConfirming(true)}
            aria-label={`Remove ${name}`}
          >
            Remove
          </Button>
        )}
      </div>
      {confirming ? (
        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[13px]">
          <span className="opacity-70">
            Remove {name}? It can’t use your log again until you connect it and allow it.
          </span>
          <Button
            variant="ghost"
            className="text-accent-2"
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(assistant.id, { onError: (err) => toast.show(err.message) })
            }
          >
            {remove.isPending ? "Removing…" : "Remove"}
          </Button>
          <Button variant="ghost" disabled={remove.isPending} onClick={() => setConfirming(false)}>
            Keep
          </Button>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Connecting an assistant (/assistant), then the ones this parent allowed.
 * A partner's assistants are theirs: they see and remove them on their phone.
 */
function Assistants() {
  const assistants = useAssistants();
  const list = assistants.data ?? [];

  return (
    <Section title="AI assistants">
      <Link
        to="/assistant"
        className="surface-btn heading flex min-h-[48px] items-center gap-2 px-4 text-[16px]"
      >
        <Sparkles className="size-5 shrink-0 text-accent-2" aria-hidden="true" />
        Connect an AI assistant
      </Link>
      <p className="mt-1.5 text-[13px] opacity-65">
        ChatGPT, Gemini, Claude, and others. Then log by saying what happened, and ask it about the
        log. Each partner connects their own.
      </p>

      <div className="mt-4">
        {assistants.isPending ? null : assistants.isError && list.length === 0 ? (
          <p className="text-[14px] opacity-65">Couldn’t load your assistants. Try again later.</p>
        ) : list.length === 0 ? (
          <p className="text-[14px] opacity-65">
            None yet. When you connect one and allow it, it shows here, and you can remove it.
          </p>
        ) : (
          <>
            {/* Every assistant holds the one scope, so what it can do is said once. */}
            <p className="text-[13px] opacity-65">Each one you allowed can:</p>
            <ul className="mb-3 list-disc pl-5 text-[13px] opacity-65">
              {ABILITIES.map((ability) => (
                <li key={ability}>{ability}</li>
              ))}
            </ul>
            <ul>
              {list.map((a) => (
                <AssistantRow key={a.id} assistant={a} />
              ))}
            </ul>
          </>
        )}
      </div>
    </Section>
  );
}

// --- Page ------------------------------------------------------------------

/** Whether this browser sends usage analytics and session recordings (per device, like night mode). */
function UsageAnalytics() {
  const [shared, setShared] = useState(analyticsAllowed);
  const recheckPush = useRecheckPushDevice();
  if (!analyticsEnabled) return null;
  return (
    <Section title="Privacy">
      <label className="flex min-h-[44px] items-center gap-3 text-[15px]">
        <input
          type="checkbox"
          checked={shared}
          onChange={(e) => {
            setAnalyticsAllowed(e.target.checked);
            setShared(e.target.checked);
            // The device's bottle alerts take up the new choice (push sent events).
            void recheckPush();
          }}
          className="size-5 accent-[var(--accent)]"
        />
        Share usage analytics and session recordings
      </label>
      <p className="text-[13px] opacity-65">
        Shows us where Babia is slow or confusing. Recordings hide every word on screen and
        everything you type. See the{" "}
        <Link to="/privacy" className="underline underline-offset-4">
          Privacy Policy
        </Link>
        .
      </p>
    </Section>
  );
}

/**
 * The version this device runs, so support can ask for it, and a reload that
 * works even when no check found a newer one (lib/app-update.tsx).
 */
function Version() {
  const { newer, reload, reloading } = useAppUpdate();
  return (
    <Section title="Version">
      <p className="text-[15px] tabular-nums">
        {CLIENT_VERSION ? `Babia ${shortVersion(CLIENT_VERSION)}` : "Babia, a development build"}
        {newer ? (
          <span className="opacity-70"> · version {shortVersion(newer)} is ready</span>
        ) : null}
      </p>
      <Button className="mt-2" disabled={reloading} onClick={() => reload("settings")}>
        {reloading ? "Reloading…" : "Reload Babia"}
      </Button>
      <p className="mt-1.5 text-[13px] opacity-65">
        Loads the newest version of Babia. Everything you logged stays.
      </p>
    </Section>
  );
}

export function Settings() {
  const { night, toggle } = useNight();
  const { data: session } = useSession();

  return (
    <div className="mx-auto max-w-[720px] pt-2">
      <div className="flex items-baseline">
        <h1 className="heading text-[26px] tracking-[-0.02em]">Settings</h1>
        <Link to="/" className="ghost-btn ml-auto px-2 py-2 text-[14px]">
          ← Log
        </Link>
      </div>
      <div className="head-rule" />

      <Group id="family" title="Your family" note="Shared with everyone in the household">
        <FamilyPlan />
        <Babies />
        <Presets />
        <Tracking />
        <Partner />
      </Group>

      <Group id="account" title="Your account" note="Yours alone. Your partner has their own.">
        <Assistants />
        <Section title="Account">
          <p className="text-[14px] opacity-65">{session?.user.email}</p>
          <Button
            variant="ghost"
            className="-ml-2 mt-1"
            onClick={() => {
              track("signed out");
              // This device stops getting the family's bottle alerts first.
              void forgetPushDevice()
                .then(() => signOut())
                .finally(resetAnalytics);
            }}
          >
            Sign out
          </Button>
        </Section>
      </Group>

      <Group id="device" title="This device" note="Only this phone or browser">
        <BottleAlerts />
        <Section title="Display">
          <label className="flex min-h-[44px] items-center gap-3 text-[15px]">
            <input
              type="checkbox"
              checked={night}
              onChange={toggle}
              className="size-5 accent-[var(--accent)]"
            />
            Night mode (dim paper)
          </label>
        </Section>
        <UsageAnalytics />
        <Version />
      </Group>

      <Group id="feedback" title="Feedback">
        <FeedbackButton
          placement="settings"
          className="surface-btn heading mt-4 flex min-h-[48px] w-full items-center gap-2 px-4 text-[16px]"
        >
          <span aria-hidden="true">💬</span>
          Send feedback
        </FeedbackButton>
        <p className="mt-1.5 text-[13px] opacity-65">
          Something broken or confusing, an idea, a question? We read every message. A connected
          assistant can send it too: say “Tell Babia…”.
        </p>
      </Group>

      <footer className="mt-8 border-t border-divider pt-4">
        <LegalLinks />
      </footer>
    </div>
  );
}
